/**
 * `ranged-kiter`: hold a firing band, give ground under pressure, and shoot
 * something the player can dodge (PRD #793).
 *
 * The role's promise to the player is that range is a position rather than a
 * safety: an archer holds a band and fires dodgeable arrows down it, and closing
 * the distance is the answer. That is why it may not kite forever. Its retreat
 * is one bounded, committed reposition inside the pressure distance
 * ({@link bandHolding}), and it fires only from inside the band with line of
 * sight, so a player who keeps walking in takes it out of its own game.
 *
 * Firing requires sight at the moment of the shot: a shot loosed at a wall is
 * spent on the wall, and an archer that cannot see you shortens the distance
 * instead of blindly firing.
 *
 * **Rng draws: one per committed reposition**, inherited from the shared band
 * policy, and none otherwise.
 */

import type { AbilityDefinition } from '../abilityCatalog';
import {
  clearCommitment,
  continueCommitment,
  holdBand,
  type RepositionBand,
} from './bandHolding';
import { currentTarget, rememberTarget } from './perception';
import type { Brain, BrainDecision } from './types';

/**
 * The archer's band, in tiles.
 *
 * The far edge sits inside the shipped arrow's nine-tile range so an archer at
 * the far edge of its band can actually reach you, and the pressure distance is
 * far enough out that a melee player is never fighting an archer that is also
 * backing off every tick.
 */
export const RANGED_KITER_BAND: RepositionBand = {
  pressureTiles: 3,
  farTiles: 7,
  repositionTiles: 3,
  commitTicks: 12,
};

export const rangedKiter: Brain = {
  id: 'ranged-kiter',

  loadoutProblems(abilities: readonly AbilityDefinition[]): string[] {
    return abilities.some((ability) => ability.shape.kind === 'projectile')
      ? []
      : ['ranged-kiter needs a projectile ability to fire'];
  },

  decide(perception, memory, rng): BrainDecision {
    const remembered = rememberTarget(memory, perception);
    const intent = {
      target: remembered.target,
      desiredRangeTiles: RANGED_KITER_BAND.farTiles,
    };
    if (perception.casting) {
      return {
        memory: remembered,
        intent: { ...intent, move: { kind: 'hold' }, cast: null },
      };
    }
    if (remembered.target === null || !remembered.lastKnown) {
      return {
        memory: clearCommitment(remembered),
        intent: { ...intent, move: { kind: 'idle' }, cast: null },
      };
    }
    const committed = continueCommitment(remembered);
    if (committed) {
      return {
        memory: committed.memory,
        intent: {
          ...intent,
          move: { kind: 'retreat', point: committed.point },
          cast: null,
        },
      };
    }
    const target = currentTarget(perception, remembered);
    if (!target?.visible) {
      // Out of sight is not out of mind: walk to where it was last seen rather
      // than firing into a wall or standing still.
      return {
        memory: clearCommitment(remembered),
        intent: {
          ...intent,
          move: { kind: 'approach', point: { ...remembered.lastKnown } },
          cast: null,
        },
      };
    }
    const band = holdBand(
      perception.self.position,
      target,
      RANGED_KITER_BAND,
      remembered,
      rng,
    );
    if (band.kind === 'retreat') {
      return {
        memory: band.memory,
        intent: { ...intent, move: { kind: 'retreat', point: band.point }, cast: null },
      };
    }
    if (band.kind === 'approach') {
      return {
        memory: clearCommitment(remembered),
        intent: { ...intent, move: { kind: 'approach', point: band.point }, cast: null },
      };
    }
    const shot = perception.abilities.find(
      (ability) =>
        ability.ready &&
        ability.shapeKind === 'projectile' &&
        ability.rangeTiles >= target.distanceTiles,
    );
    return {
      memory: clearCommitment(remembered),
      intent: {
        ...intent,
        move: { kind: 'hold' },
        cast: shot ? { abilityId: shot.id, aim: { ...target.position } } : null,
      },
    };
  },
};
